import React, { useEffect, useState } from "react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Factory, TrendingUp, Boxes, Gauge, TrendingDown, Wallet, Percent } from "lucide-react";
import api from "../lib/api";
import { Card } from "../lib/ui";
import { fmtRp, fmtNum } from "../lib/constants";
import { useT } from "../lib/i18n";

const compactRp = (v) => {
  const n = Number(v) || 0, a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(1)}M`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}jt`;
  if (a >= 1e3) return `${Math.round(n / 1e3)}rb`;
  return `${n}`;
};
const shortDate = (d) => (d || "").slice(5);

function Widget({ icon: Icon, title, subtitle, children, span, testid }) {
  return (
    <Card className={`p-4 sm:p-5 ${span || ""}`} data-testid={testid}>
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-light text-primary"><Icon size={16} /></span>
        <div>
          <h3 className="font-heading text-sm font-extrabold text-ink">{title}</h3>
          {subtitle && <p className="text-[11px] text-ink-mute">{subtitle}</p>}
        </div>
      </div>
      {children}
    </Card>
  );
}

const Empty = ({ label }) => <div className="flex h-[180px] items-center justify-center text-sm text-ink-mute">{label}</div>;

export default function ProductionInsights() {
  const { t } = useT();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/production/insights").then(({ data }) => setD(data)).catch(() => setD({})); }, []);

  if (!d) return <div className="mt-8 h-40 animate-pulse rounded-3xl bg-stone-100" data-testid="production-insights-loading" />;

  const yieldTrend = d.yield_trend || [];
  const top = d.top_produced || [];
  const eff = d.efficiency || {};
  const hpp = d.hpp_composition || {};
  const hppTotal = hpp.total || 0;
  const hppParts = [
    { key: "material", label: t("Material Cost"), value: hpp.material || 0, color: "#ea580c" },
    { key: "labor", label: t("Labor Cost"), value: hpp.labor || 0, color: "#3b82f6" },
    { key: "overhead", label: t("Overhead Cost"), value: hpp.overhead || 0, color: "#a8a29e" },
  ];
  const effData = [
    { name: t("Planned"), value: eff.planned_total || 0, fill: "#a8a29e" },
    { name: t("Actual"), value: eff.actual_total || 0, fill: "#ea580c" },
  ];

  return (
    <div className="mt-8" data-testid="production-insights">
      <div className="mb-4 flex items-center gap-2">
        <Factory size={18} className="text-primary" />
        <h2 className="font-heading text-sm font-extrabold uppercase tracking-wide text-ink-soft">{t("Production Insights")}</h2>
      </div>

      {/* KPI row */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <KpiCard icon={Wallet} label={t("Production Value Today")} value={fmtRp(d.production_value_today)} tone="primary" testid="pi-value-today" />
        <KpiCard icon={Factory} label={t("Production Value This Month")} value={fmtRp(d.production_value_month)} tone="info" testid="pi-value-month" />
        <KpiCard icon={Gauge} label={t("Production Efficiency")} value={eff.efficiency_pct != null ? `${eff.efficiency_pct}%` : "—"} tone="success" testid="pi-efficiency" />
        <KpiCard icon={TrendingDown} label={t("Production Loss Value")} value={fmtRp(d.production_loss_value)} tone="danger" testid="pi-loss" />
        <KpiCard icon={Wallet} label={t("Avg HPP per Unit")} value={d.avg_hpp_per_unit != null ? fmtRp(d.avg_hpp_per_unit) : "—"} tone="primary" testid="pi-avg-hpp" />
        <KpiCard icon={Percent} label={t("Avg Gross Margin")} value={d.avg_gross_margin_pct != null ? `${d.avg_gross_margin_pct}%` : "—"} tone="success" testid="pi-avg-margin" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Yield trend */}
        <Widget icon={TrendingUp} title={t("Production Yield Trend")} subtitle={t("Average yield % · last 30 days")} testid="pi-yield-trend">
          {yieldTrend.some((p) => p.yield != null) ? (
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={yieldTrend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
                <defs><linearGradient id="piYieldGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                </linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#9b8e82" }} interval={5} />
                <YAxis tick={{ fontSize: 10, fill: "#9b8e82" }} width={34} domain={[0, 100]} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Area type="monotone" dataKey="yield" stroke="#10b981" strokeWidth={2.5} fill="url(#piYieldGrad)" connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Empty label={t("No confirmed production yet")} />}
        </Widget>

        {/* Efficiency planned vs actual */}
        <Widget icon={Gauge} title={t("Production Efficiency")} subtitle={t("Planned vs actual output · this month")} testid="pi-efficiency-chart">
          {(eff.planned_total || 0) > 0 ? (
            <div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={effData} layout="vertical" margin={{ top: 5, right: 12, left: 12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#9b8e82" }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: "#57534e" }} width={64} />
                  <Tooltip formatter={(v) => fmtNum(v)} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                    {effData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="mt-1 text-center text-sm font-bold text-success-text" data-testid="pi-efficiency-pct">{eff.efficiency_pct}% {t("efficiency")}</p>
            </div>
          ) : <Empty label={t("No confirmed production yet")} />}
        </Widget>

        {/* HPP Composition */}
        <Widget icon={Wallet} title={t("HPP Composition")} subtitle={t("Material / Labor / Overhead · this month")} testid="pi-hpp-composition">
          {hppTotal > 0 ? (
            <div className="py-2">
              <div className="mb-3 flex h-3 overflow-hidden rounded-full bg-stone-100">
                {hppParts.map((p) => p.value > 0 && (
                  <div key={p.key} style={{ width: `${(p.value / hppTotal) * 100}%`, background: p.color }} />
                ))}
              </div>
              <div className="space-y-2">
                {hppParts.map((p) => (
                  <div key={p.key} className="flex items-center justify-between text-sm" data-testid={`pi-hpp-${p.key}`}>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} /><span className="text-ink-soft">{p.label}</span></span>
                    <span className="font-semibold text-ink">{fmtRp(p.value)} <span className="text-ink-mute">({Math.round((p.value / hppTotal) * 100)}%)</span></span>
                  </div>
                ))}
                <div className="flex items-center justify-between border-t border-line pt-2 text-sm font-bold"><span>{t("Total HPP")}</span><span className="text-primary">{fmtRp(hppTotal)}</span></div>
              </div>
            </div>
          ) : <Empty label={t("No confirmed production yet")} />}
        </Widget>

        {/* Top produced */}
        <Widget icon={Boxes} title={t("Top Produced Items")} subtitle={t("Highest output this month")} testid="pi-top-produced">
          {top.length ? (
            <div className="space-y-2.5 py-1">
              {top.map((p, i) => {
                const max = top[0].qty || 1;
                return (
                  <div key={i} data-testid={`pi-top-${i}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-semibold text-ink">{p.product_name}</span>
                      <span className="font-bold text-ink">{fmtNum(p.qty)} · {p.batches}x</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(6, (p.qty / max) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Empty label={t("No confirmed production yet")} />}
        </Widget>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "primary", testid }) {
  const tones = { primary: "bg-primary-light text-primary", info: "bg-blue-50 text-blue-600", success: "bg-success-light text-success-text", danger: "bg-danger-light text-danger-text" };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
        <div className="min-w-0"><p className="truncate text-xs font-semibold uppercase text-ink-mute">{label}</p><p className="truncate text-lg font-bold text-ink">{value}</p></div>
      </div>
    </Card>
  );
}
